/**
 * Engagement analytics: page views and time on page.
 * Google tag is loaded in index.html; this module sends page_view on route
 * changes (SPA) and time_on_page events. Override with .env if needed.
 */

const MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || "G-BKCNQNSSB8";

declare global {
  interface Window {
    gtag?: (
      command: "config" | "event" | "js",
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

function ensureDataLayer(): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
}

function ensureGtag(): void {
  if (typeof window === "undefined" || window.gtag) return;
  window.gtag = function gtag(
    command: "config" | "event" | "js",
    targetId: string,
    config?: Record<string, unknown>
  ) {
    window.dataLayer?.push(arguments);
  };
}

/**
 * Loads the Google Analytics gtag script if MEASUREMENT_ID is set.
 * Safe to call multiple times; script loads once.
 */
export function loadGoogleAnalytics(): void {
  if (typeof window === "undefined" || !MEASUREMENT_ID) return;
  ensureDataLayer();
  ensureGtag();

  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  (window.gtag as (c: string, ...a: unknown[]) => void)?.("js", new Date());
  window.gtag?.("config", MEASUREMENT_ID, {
    send_page_view: false, // We send page_view manually on route change (SPA)
  });
}

/**
 * Track a page view (for SPA route changes).
 * Use full path including search: e.g. pathname + search.
 */
export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("config", MEASUREMENT_ID, {
    page_path: path || "/",
    page_title: title || document.title,
  });
}

/**
 * Track time spent on a page (e.g. when user leaves or tab becomes hidden).
 * Shows in GA4 as a custom event; you can build reports on engagement_time_seconds.
 */
export function trackTimeOnPage(path: string, timeSpentSeconds: number): void {
  if (!MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  if (timeSpentSeconds < 1) return; // Ignore very short stays
  window.gtag("event", "time_on_page", {
    page_path: path || "/",
    engagement_time_seconds: Math.round(timeSpentSeconds),
  });
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}
